import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon,
  ChevronsDownUp, ChevronsUpDown,
  Loader2, RefreshCw, AlertTriangle, TrendingUp, Calendar, Layers, Euro
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { useSociete } from '../context/SocieteContext';

// ── Helpers ──────────────────────────────────────────────────────────────────

const MOIS_FR = [
  'Janvier','Février','Mars','Avril','Mai','Juin',
  'Juillet','Août','Septembre','Octobre','Novembre','Décembre',
];

function fmtEur(v) {
  return (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}
function fmtEurDec(v) {
  return (v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function pct(v) {
  return Math.round(v * 100) + ' %';
}

// ── Card de synthèse ─────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, sub, color = 'var(--primary-solid)', accent }) {
  return (
    <div className="flex-1 min-w-40 rounded-2xl border px-5 py-4"
      style={{ borderColor: 'var(--border)', background: accent || 'white' }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 rounded-lg" style={{ background: `${color}18` }}>
          <Icon size={15} style={{ color }} />
        </div>
        <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
      <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--text)' }}>{value}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub}</p>}
    </div>
  );
}

// ── Ligne gamme ──────────────────────────────────────────────────────────────

function GammeRow({ gamme, taux, nb_jours, nb_jours_cal }) {
  const ca_potentiel   = gamme.nb_materiels * gamme.prix_moyen_jour * nb_jours * taux;
  const jours_pot      = gamme.nb_materiels * nb_jours;
  const max_jours      = nb_jours_cal * gamme.nb_materiels;
  const taux_loc       = max_jours > 0 ? gamme.jours_loues / max_jours : null;
  const taux_real      = gamme.ca_base > 0 ? gamme.ca_facture / gamme.ca_base : null;
  const sep = {};
  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td className="pl-10 pr-3 py-2.5">
        <span className="text-sm" style={{ color: 'var(--text)' }}>{gamme.gamme_nom}</span>
        {gamme.nb_tiers > 0 && (
          <span className="ml-2 text-xs" style={{ color: 'var(--text-disabled)' }}>
            {gamme.nb_tiers} tier{gamme.nb_tiers > 1 ? 's' : ''}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-center">
        <span className="inline-flex items-center justify-center min-w-8 h-7 px-2 rounded-lg text-sm font-bold"
          style={{
            background: gamme.nb_materiels > 0 ? 'var(--primary-50)' : '#F3F4F6',
            color:      gamme.nb_materiels > 0 ? 'var(--primary-solid)' : '#9CA3AF',
          }}>
          {gamme.nb_materiels}
        </span>
      </td>
      <td className="px-3 py-2.5 text-center text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {gamme.prix_moyen_jour > 0 ? fmtEurDec(gamme.prix_moyen_jour) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
      </td>
      <td />
      <td className="px-3 py-2.5 text-center text-sm tabular-nums" style={{ color: 'var(--text-muted)', ...sep }}>{jours_pot > 0 ? jours_pot : '—'}</td>
      <td className="px-3 py-2.5 text-center text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {gamme.jours_loues > 0 ? gamme.jours_loues : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
      </td>
      <td className="px-3 py-2.5 text-center text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {taux_loc !== null ? pct(taux_loc) : '—'}
      </td>
      <td />
      <td className="px-3 py-2.5 text-center text-sm font-semibold tabular-nums" style={{ color: ca_potentiel > 0 ? 'var(--text)' : 'var(--text-disabled)' }}>
        {ca_potentiel > 0 ? fmtEur(ca_potentiel) : '—'}
      </td>
      <td className="px-3 py-2.5 text-center text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {gamme.ca_facture > 0 ? fmtEur(gamme.ca_facture) : <span style={{ color: 'var(--text-disabled)' }}>—</span>}
      </td>
      <td className="px-4 py-2.5 text-center text-sm tabular-nums" style={{ color: 'var(--text-muted)' }}>
        {taux_real !== null ? pct(taux_real) : '—'}
      </td>
    </tr>
  );
}

// ── Ligne catégorie (accordion contrôlé depuis le parent) ────────────────────

function CategorieRow({ cat, taux, nb_jours, nb_jours_cal, open, onToggle, sortConfig, getSortValue }) {
  const ca_cat        = cat.gammes.reduce((acc, g) => acc + g.nb_materiels * g.prix_moyen_jour * nb_jours * taux, 0);
  const prix_moy_cat  = cat.total_parc > 0
    ? cat.gammes.reduce((acc, g) => acc + g.prix_moyen_jour * g.nb_materiels, 0) / cat.total_parc
    : 0;
  const jours_pot_cat = cat.total_parc * nb_jours;
  const taux_occ_cat  = (nb_jours_cal * cat.total_parc) > 0
    ? cat.total_jours_loues / (nb_jours_cal * cat.total_parc)
    : null;
  const taux_real_cat = cat.total_ca_base > 0 ? cat.total_ca_facture / cat.total_ca_base : null;
  const s = { background: 'var(--bg-subtle, #F8F9FA)', borderBottom: '1px solid var(--border)' };
  const tc = 'px-3 py-3 text-center text-sm font-semibold tabular-nums';

  const sortedGammes = sortConfig.col
    ? [...cat.gammes].sort((a, b) => {
        const va = getSortValue(a, sortConfig.col);
        const vb = getSortValue(b, sortConfig.col);
        return sortConfig.dir === 'asc' ? va - vb : vb - va;
      })
    : cat.gammes;

  return (
    <>
      <tr onClick={onToggle} style={{ cursor: 'pointer', ...s }}>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="p-0.5 rounded transition-transform" style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
              <ChevronRightIcon size={14} style={{ color: 'var(--text-muted)' }} />
            </div>
            <span className="text-sm font-bold" style={{ color: 'var(--text)' }}>{cat.categorie_nom}</span>
            <span className="ml-1 text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: '#E0E7FF', color: '#4338CA' }}>
              {cat.gammes.length} gamme{cat.gammes.length > 1 ? 's' : ''}
            </span>
          </div>
        </td>
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{cat.total_parc}</td>
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{prix_moy_cat > 0 ? fmtEurDec(prix_moy_cat) : '—'}</td>
        <td />
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{jours_pot_cat > 0 ? jours_pot_cat : '—'}</td>
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{cat.total_jours_loues > 0 ? cat.total_jours_loues : '—'}</td>
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{taux_occ_cat !== null ? pct(taux_occ_cat) : '—'}</td>
        <td />
        <td className={tc} style={{ color: ca_cat > 0 ? 'var(--primary-solid)' : 'var(--text-disabled)' }}>{ca_cat > 0 ? fmtEur(ca_cat) : '—'}</td>
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{cat.total_ca_facture > 0 ? fmtEur(cat.total_ca_facture) : '—'}</td>
        <td className={tc} style={{ color: 'var(--text-muted)' }}>{taux_real_cat !== null ? pct(taux_real_cat) : '—'}</td>
      </tr>
      {open && sortedGammes.map(g => (
        <GammeRow key={g.gamme_id} gamme={g} taux={taux} nb_jours={nb_jours} nb_jours_cal={nb_jours_cal} />
      ))}
    </>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────

export default function PotentielLocatif() {
  const today = new Date();
  const { activeAgences } = useSociete();
  const [mois, setMois]       = useState(today.getMonth() + 1);
  const [annee, setAnnee]     = useState(today.getFullYear());
  const [exclureOR, setExclureOR] = useState(false);
  const [taux, setTaux]       = useState(0.45);   // 45 % par défaut
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCats, setOpenCats] = useState(new Set()); // vide = tout replié par défaut
  const [sortConfig, setSortConfig] = useState({ col: null, dir: 'asc' });

  const toggleSort = (col) => setSortConfig(s =>
    s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'desc' }
  );

  function getSortValue(g, col) {
    const nb_j    = data?.nb_jours_ouvrables  || 0;
    const nb_j_c  = data?.nb_jours_calendaires || 0;
    switch (col) {
      case 'nb_mat':    return g.nb_materiels;
      case 'prix':      return g.prix_moyen_jour;
      case 'jours_pot': return g.nb_materiels * nb_j;
      case 'jours_lou': return g.jours_loues;
      case 'taux_occ':  return nb_j_c * g.nb_materiels > 0 ? g.jours_loues / (nb_j_c * g.nb_materiels) : 0;
      case 'ca_pot':    return g.nb_materiels * g.prix_moyen_jour * nb_j * taux;
      case 'ca_fact':   return g.ca_facture;
      case 'taux_real': return g.ca_base > 0 ? g.ca_facture / g.ca_base : 0;
      default: return 0;
    }
  }
  const sliderRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getPotentielLocatif({ mois, annee, exclure_or: exclureOR });
      setData(r.data);
      setOpenCats(new Set()); // replier tout au rechargement
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [mois, annee, exclureOR, activeAgences]);

  useEffect(() => { load(); }, [load]);

  const prevMois = () => {
    if (mois === 1) { setMois(12); setAnnee(a => a - 1); }
    else setMois(m => m - 1);
  };
  const nextMois = () => {
    if (mois === 12) { setMois(1); setAnnee(a => a + 1); }
    else setMois(m => m + 1);
  };

  const allIds = data ? data.categories.map(c => c.categorie_id) : [];
  const allOpen = allIds.length > 0 && allIds.every(id => openCats.has(id));

  const toggleAllCats = () => {
    if (allOpen) {
      setOpenCats(new Set());
    } else {
      setOpenCats(new Set(allIds));
    }
  };

  const toggleCat = (catId) => {
    setOpenCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  // CA potentiel total avec le taux courant du slider
  const ca_potentiel_total = data
    ? data.categories.reduce((acc, cat) =>
        acc + cat.gammes.reduce((a, g) => a + g.nb_materiels * g.prix_moyen_jour * data.nb_jours_ouvrables * taux, 0), 0)
    : 0;

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 48px)', margin: '-24px', minHeight: 0 }}>

      {/* ── En-tête ── */}
      <div className="flex-shrink-0 px-6 py-4 border-b bg-white flex items-center gap-4 flex-wrap"
        style={{ borderColor: 'var(--border)' }}>

        {/* Titre */}
        <div>
          <h1 className="text-base font-bold" style={{ color: 'var(--text)' }}>Potentiel locatif</h1>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Parc Location Courte Durée</p>
        </div>

        {/* Sélecteur mois */}
        <div className="flex items-center gap-1 px-3 py-1.5 rounded-xl border" style={{ borderColor: 'var(--border)' }}>
          <button data-testid="month-prev-btn" onClick={prevMois} className="p-1 rounded-lg hover:bg-gray-100">
            <ChevronLeft size={14} />
          </button>
          <span className="text-sm font-semibold w-32 text-center" style={{ color: 'var(--text)' }}>
            {MOIS_FR[mois - 1]} {annee}
          </span>
          <button data-testid="month-next-btn" onClick={nextMois} className="p-1 rounded-lg hover:bg-gray-100">
            <ChevronRight size={14} />
          </button>
        </div>

        {/* Toggle OR */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <div
            onClick={() => setExclureOR(v => !v)}
            className="relative w-9 h-5 rounded-full transition-colors"
            style={{ background: exclureOR ? '#EF4444' : '#D1D5DB' }}>
            <div className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all"
              style={{ left: exclureOR ? '18px' : '2px' }} />
          </div>
          <span className="text-xs font-medium flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
            {exclureOR && <AlertTriangle size={11} style={{ color: '#EF4444' }} />}
            Exclure OR ouverts &gt; 1 mois
            {data?.nb_or_exclus > 0 && exclureOR && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{ background: '#FEE2E2', color: '#B91C1C' }}>
                {data.nb_or_exclus}
              </span>
            )}
          </span>
        </label>

        {/* Slider taux */}
        <div className="flex items-center gap-3 ml-auto">
          <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Taux d'occupation</span>
          <input
            ref={sliderRef}
            type="range" min={0} max={100} step={1}
            value={Math.round(taux * 100)}
            onChange={e => setTaux(Number(e.target.value) / 100)}
            className="w-40 accent-[var(--primary-solid)]"
            style={{ accentColor: 'var(--primary-solid)' }}
          />
          <span className="text-base font-bold tabular-nums w-12 text-right"
            style={{ color: 'var(--primary-solid)' }}>
            {Math.round(taux * 100)} %
          </span>
        </div>

        <button onClick={load} disabled={loading}
          className="p-1.5 rounded-lg border hover:bg-gray-50" style={{ borderColor: 'var(--border)' }}>
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>

        {/* Toggle déplier / replier tout */}
        {data && data.categories.length > 0 && (
          <button
            data-testid="toggle-all-categories-btn"
            onClick={toggleAllCats}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium hover:bg-gray-50 transition-colors"
            style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}
            title={allOpen ? 'Tout replier' : 'Tout déplier'}
          >
            {allOpen
              ? <><ChevronsDownUp size={13} /> Tout replier</>
              : <><ChevronsUpDown size={13} /> Tout déplier</>
            }
          </button>
        )}
      </div>

      {/* ── Corps ── */}
      {loading && !data ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={28} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : data ? (
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* Cards */}
          <div className="flex gap-4 flex-wrap">
            <SummaryCard
              icon={Layers}
              label="Parc LCD total"
              value={data.parc_total}
              sub={`matériel${data.parc_total !== 1 ? 's' : ''} en location courte durée`}
              color="#6366F1"
            />
            <SummaryCard
              icon={TrendingUp}
              label={`CA potentiel (${Math.round(taux * 100)} %)`}
              value={fmtEur(ca_potentiel_total)}
              sub={`${MOIS_FR[mois - 1]} ${annee}`}
              color="var(--primary-solid)"
              accent="var(--primary-50)"
            />
            <SummaryCard
              icon={Calendar}
              label="Jours ouvrables"
              value={data.nb_jours_ouvrables}
              sub={`dont ${data.dates_feries_count} jour${data.dates_feries_count !== 1 ? 's' : ''} férié${data.dates_feries_count !== 1 ? 's' : ''} exclu${data.dates_feries_count !== 1 ? 's' : ''}`}
              color="#0EA5E9"
            />
            <SummaryCard
              icon={Euro}
              label="Prix moyen / jour"
              value={fmtEurDec(data.prix_moyen_parc)}
              sub="moyenne pondérée du parc"
              color="#10B981"
            />
          </div>

          {/* Tableau */}
          {data.categories.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-24 rounded-2xl border border-dashed"
              style={{ borderColor: 'var(--border)' }}>
              <Layers size={36} style={{ color: 'var(--text-disabled)' }} />
              <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Aucune gamme de location configurée
              </p>
              <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-disabled)' }}>
                Configurez des gammes avec des modèles affectés dans Locations → Gammes
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
              <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '50px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '50px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '125px' }} />
                  <col style={{ width: '125px' }} />
                </colgroup>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'var(--bg-subtle, #F9FAFB)', borderBottom: '1px solid var(--border)' }}>
                    <th className="text-left px-4 py-3 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Catégorie / Gamme</th>
                    {[
                      { col: 'nb_mat',    label: 'Matériels' },
                      { col: 'prix',      label: 'Prix moy / j' },
                      { col: null,        label: '' },
                      { col: 'jours_pot', label: 'Jours potentiels' },
                      { col: 'jours_lou', label: 'Jours loués' },
                      { col: 'taux_occ',  label: "Taux d'occupation" },
                      { col: null,        label: '' },
                      { col: 'ca_pot',    label: 'CA potentiel' },
                      { col: 'ca_fact',   label: 'CA facturé' },
                      { col: 'taux_real', label: 'Taux réalisation' },
                    ].map(({ col, label }, i) => (
                      <th key={i}
                        onClick={col ? () => toggleSort(col) : undefined}
                        className="px-3 py-3 text-xs font-semibold text-center select-none"
                        style={{
                          color: sortConfig.col === col ? 'var(--primary-solid)' : 'var(--text-muted)',
                          cursor: col ? 'pointer' : 'default',
                          userSelect: 'none',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {label}
                        {col && (
                          <span className="ml-1" style={{ opacity: sortConfig.col === col ? 1 : 0.3 }}>
                            {sortConfig.col === col ? (sortConfig.dir === 'asc' ? ' ▲' : ' ▼') : ' ▲'}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.categories.map(cat => (
                    <CategorieRow
                      key={cat.categorie_id}
                      cat={cat}
                      taux={taux}
                      nb_jours={data.nb_jours_ouvrables}
                      nb_jours_cal={data.nb_jours_calendaires}
                      open={openCats.has(cat.categorie_id)}
                      onToggle={() => toggleCat(cat.categorie_id)}
                      sortConfig={sortConfig}
                      getSortValue={getSortValue}
                    />
                  ))}
                  {/* Ligne totale */}
                  {(() => {
                    const sep    = {};
                    const max_j  = data.nb_jours_calendaires * data.parc_total;
                    const t_loc  = max_j > 0 ? data.jours_loues_total / max_j : null;
                    const t_real = data.ca_base_total > 0 ? data.ca_facture_total / data.ca_base_total : null;
                    const jours_pot_total = data.parc_total * data.nb_jours_ouvrables;
                    return (
                      <tr style={{ background: 'var(--primary-50)', borderTop: '2px solid var(--primary-solid)' }}>
                        <td className="px-4 py-3 text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>TOTAL</td>
                        <td className="px-3 py-3 text-center text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{data.parc_total}</td>
                        <td className="px-3 py-3 text-center text-sm tabular-nums font-semibold" style={{ color: 'var(--primary-solid)' }}>{fmtEurDec(data.prix_moyen_parc)}</td>
                        <td />
                        <td className="px-3 py-3 text-center text-sm font-bold" style={{ color: 'var(--primary-solid)', ...sep }}>{jours_pot_total}</td>
                        <td className="px-3 py-3 text-center text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{data.jours_loues_total > 0 ? data.jours_loues_total : '—'}</td>
                        <td className="px-3 py-3 text-center text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{t_loc !== null ? pct(t_loc) : '—'}</td>
                        <td />
                        <td className="px-3 py-3 text-center text-base font-bold tabular-nums" style={{ color: 'var(--primary-solid)', ...sep }}>{fmtEur(ca_potentiel_total)}</td>
                        <td className="px-3 py-3 text-center text-sm font-bold tabular-nums" style={{ color: 'var(--primary-solid)' }}>{data.ca_facture_total > 0 ? fmtEur(data.ca_facture_total) : '—'}</td>
                        <td className="px-4 py-3 text-center text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>{t_real !== null ? pct(t_real) : '—'}</td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
